import { makeStyles } from '@material-ui/core/styles'
import {
  interactiveCardFocusStyles,
  interactiveCardLinkResetStyles,
} from 'lib/styles/interactive-card-hover'

const APP_BAR_HEIGHT = 61
const IMPERSONATION_OFFSET_SM = 30
const IMPERSONATION_OFFSET_XS = 70

const getScrollMarginTop = assumedUser =>
  assumedUser ? APP_BAR_HEIGHT + IMPERSONATION_OFFSET_SM : APP_BAR_HEIGHT

const CARD_BORDER = '#E0E0E0'
const ICON_CIRCLE_BACKGROUND = '#EBF0F2'
const ICON_CIRCLE_COLOR = '#172024'

const useStyles = makeStyles(theme => ({
  promoBanner: {
    marginBottom: 0,
  },
  container: {
    margin: '2rem auto 6rem',
    maxWidth: 832,
    padding: theme.spacing(0, 2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    boxSizing: 'border-box',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0, 1),
      gap: theme.spacing(2.5),
    },
  },
  metricListCard: {
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    overflow: 'hidden',
  },
  metricItem: ({ assumedUser }) => ({
    scrollMarginTop: getScrollMarginTop(assumedUser),
    [theme.breakpoints.only('xs')]: {
      scrollMarginTop: assumedUser
        ? APP_BAR_HEIGHT + IMPERSONATION_OFFSET_XS
        : APP_BAR_HEIGHT,
    },
    '& + &': {
      borderTop: `1px solid ${CARD_BORDER}`,
    },
  }),
  metricCardHeader: {
    display: 'flex',
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(2, 2.5),
    cursor: 'pointer',
    textAlign: 'left',
    color: 'inherit',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.75, 2),
      gap: theme.spacing(1.5),
    },
  },
  metricCardHeaderExpanded: {
    borderBottom: `1px solid ${CARD_BORDER}`,
  },
  metricCardHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: 0,
    flex: '1 1 0',
    overflow: 'hidden',
  },
  metricCardHeaderText: {
    minWidth: 0,
    flex: '1 1 auto',
  },
  metricCardHeaderRight: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flex: '0 0 auto',
    flexShrink: 0,
  },
  metricCardHeaderChevron: {
    flexShrink: 0,
    color: 'rgba(0, 0, 0, 0.38)',
    fontSize: 20,
    width: 20,
    height: 20,
  },
  metricIconCircle: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: ICON_CIRCLE_BACKGROUND,
    color: ICON_CIRCLE_COLOR,
    '& svg, & .MuiSvgIcon-root': {
      fontSize: 16,
      width: 16,
      height: 16,
      color: ICON_CIRCLE_COLOR,
    },
  },
  metricTitle: {
    margin: 0,
    fontSize: '0.9375rem',
    fontWeight: 600,
    lineHeight: 1.3,
    color: '#172024',
  },
  metricDescription: {
    margin: theme.spacing(0.5, 0, 0),
    fontSize: '0.8125rem',
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  },
  metricBody: {},
  metricBodyDivider: {
    height: 1,
    backgroundColor: CARD_BORDER,
  },
  metricBodySection: {
    padding: theme.spacing(2, 2.5),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.75, 2),
    },
    '&:first-of-type': {
      paddingTop: theme.spacing(1.5),
    },
    '&:last-of-type': {
      paddingBottom: theme.spacing(2),
    },
  },
  metricBodySectionInsight: {
    paddingTop: theme.spacing(2.5),
    paddingBottom: theme.spacing(2.5),
  },
  metricBodySectionRecommended: {
    paddingTop: theme.spacing(2.5),
    paddingBottom: theme.spacing(2),
  },
  metricBodySectionTitle: {
    marginBottom: theme.spacing(1.5),
  },
  metricValueRow: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
  },
  metricValue: {
    fontWeight: 700,
    fontSize: '1.75rem',
    lineHeight: 1.1,
    color: '#172024',
  },
  scaleSection: {
    marginBottom: 0,
  },
  scaleTrack: {
    position: 'relative',
    height: 6,
    borderRadius: 3,
    backgroundColor: '#EBF0F2',
    marginBottom: theme.spacing(1),
  },
  scaleCoachMarker: {
    position: 'absolute',
    top: '50%',
    width: 10,
    height: 10,
    borderRadius: '50%',
    backgroundColor: '#00234B',
    border: '2px solid #FFFFFF',
    transform: 'translate(-50%, -50%)',
    zIndex: 2,
  },
  scaleTick: {
    position: 'absolute',
    top: -3,
    bottom: -3,
    width: 1,
    backgroundColor: '#C5D0D6',
    transform: 'translateX(-50%)',
    zIndex: 1,
  },
  scaleLabelsRow: {
    position: 'relative',
    height: 16,
  },
  scaleLabel: {
    position: 'absolute',
    top: 0,
    fontSize: '0.625rem',
    fontWeight: 600,
    letterSpacing: '0.03em',
    lineHeight: 1.3,
    color: '#8A9AA3',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  },
  scaleLabelCenter: {
    transform: 'translateX(-50%)',
  },
  scaleLabelEnd: {
    transform: 'translateX(-100%)',
  },
  scaleValuesRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2.5),
    flexShrink: 0,
  },
  scaleValueGroup: {
    textAlign: 'right',
  },
  scaleValueLabel: {
    fontSize: '0.625rem',
    fontWeight: 600,
    letterSpacing: '0.03em',
    lineHeight: 1.3,
    color: '#8A9AA3',
    textTransform: 'uppercase',
    marginBottom: 1,
  },
  scaleValue: {
    fontSize: '0.8125rem',
    fontWeight: 700,
    lineHeight: 1.3,
    color: '#172024',
  },
  recommendedList: {
    display: 'flex',
    flexDirection: 'column',
    margin: theme.spacing(0, -1),
  },
  recommendedRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.25, 1),
    borderRadius: 8,
    cursor: 'pointer',
    ...interactiveCardLinkResetStyles,
    '&:hover': {
      backgroundColor: 'rgba(0, 35, 75, 0.04)',
    },
    ...interactiveCardFocusStyles,
    '& + &': {
      borderTop: `1px solid ${CARD_BORDER}`,
    },
    '& *': {
      textDecoration: 'none',
    },
  },
  recommendedRowCopy: {
    flex: '1 1 auto',
    minWidth: 0,
  },
  recommendedRowTitle: {
    margin: 0,
    fontWeight: 600,
    fontSize: '0.9375rem',
    lineHeight: 1.3,
    color: '#172024',
  },
  recommendedRowTitleCompleted: {
    textDecoration: 'line-through',
    color: theme.palette.text.secondary,
  },
  recommendedRowDescription: {
    margin: theme.spacing(0.5, 0, 0),
    fontSize: '0.8125rem',
    lineHeight: 1.45,
    color: theme.palette.text.secondary,
  },
  recommendedRowChevron: {
    flexShrink: 0,
    color: 'rgba(0, 0, 0, 0.38)',
    fontSize: 20,
    width: 20,
    height: 20,
  },
  recommendedIconCircle: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: ICON_CIRCLE_BACKGROUND,
    color: ICON_CIRCLE_COLOR,
    '& svg, & .MuiSvgIcon-root': {
      fontSize: 14,
      color: ICON_CIRCLE_COLOR,
    },
  },
  recommendedIconCircleCompleted: {
    backgroundColor: '#BEF1BB',
    color: '#00210B',
    '& svg, & .MuiSvgIcon-root': {
      color: '#00210B',
      fill: 'currentColor',
    },
  },
  recommendedDoneBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flexShrink: 0,
    borderRadius: 999,
    padding: theme.spacing(0.375, 1),
    fontSize: '0.6875rem',
    fontWeight: 600,
    lineHeight: 1.3,
    whiteSpace: 'nowrap',
    color: '#00210B',
    backgroundColor: '#BEF1BB',
  },
  recommendedDoneBadgeIcon: {
    flexShrink: 0,
    fontSize: 14,
    width: 14,
    height: 14,
    color: '#00210B',
  },
  sampleNotice: {
    margin: 0,
    padding: theme.spacing(2, 2.5),
    borderRadius: 10,
    fontSize: '0.8125rem',
    lineHeight: 1.45,
    color: theme.palette.text.secondary,
    backgroundColor: '#F5F8FF',
    border: '1px solid #E8EDF5',
  },
  footer: {
    margin: 0,
    fontSize: '0.8125rem',
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
}))

export default useStyles
